/**
 * @file Sticky approval footer of the Mapping Review panel: a live summary
 * recomputed from the session (an entity import shows columns mapped · rows
 * in · rejected → will write; a whole document shows fields · references ·
 * tables and the total rows it will write), a guided-queue warning band, the
 * "Create new form version" Checkbox (enabled only for a formVersion-bound
 * apply, with a Select of the form type's allowed next states), and a
 * size-dominant Approve separated from Cancel by a divider. The warning
 * jump-links are guided queues: each click advances to the NEXT offender,
 * cycles, routes to the owning group/column, and moves keyboard focus onto the
 * target region (not just scrollIntoView). Trust accounting is legible — the
 * low-confidence link names the threshold inline ("below 50%"), distinguishes
 * still-low-and-untouched from items the user has addressed, and resolves to
 * "All flagged mappings reviewed ✓" once nothing low-and-untouched remains.
 * The jump-links wrap their text, so a phone-width panel never clips them.
 * For a dropped-file import bound to the draft form the run just created, a
 * Target form-type Select lets the user re-route the import; cancelling
 * discards the run's draft.
 * Owns the single resolution sequence for both kinds: `buildEditedProcessDocument`
 * is persisted to Storage FIRST and only then does the bare `approvalResolved`
 * POST go out; all user edits travel in the saved document's decisions overlay.
 * `agentApprovalState.submitting` is raised for the whole save+POST window so
 * the chat card and this footer are single-flight; an inline stage label tracks
 * which phase is running ("Saving edits…" then "Submitting…") and a persistent
 * inline error Alert (not just the transient toast) survives until the user
 * retries or dismisses it. The resolution is recorded on
 * `agentApprovalState.resolved` as an in-memory transcript card that survives
 * only until the next loop start / clear. A `newFormVersionId` in the response
 * navigates via `formActions.navigateToVersion`.
 */
import { CSSProperties, FC, useContext, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  InputNumber,
  Select,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import { useSnapshot } from 'valtio';
import type { Snapshot } from 'valtio';
import { CONFIDENCE_WARN_THRESHOLD, formStateDescription } from '#types';
import type {
  AgenticApprovalResolvedResponse,
  DateLocale,
  FormStateId,
  LocaleSource,
  NumberLocale,
} from '#types';
import { formActions, formStore } from '@/store';
import { callRunLanguageModel } from '@/services/cloudRun';
import { saveProcessDocument } from '@/storage/saveProcessDocument';
import { MessageContext } from '@/providers/MessageContext';
import { AIContext } from '@/providers/AIContext';
import { useAgentContext } from '@/use/useAgentContext';
import { useFormTypes } from '@/use/useFormTypes';
import FlexBox from '../../atoms/FlexBox';
import FlexCol from '../../atoms/FlexCol';
import { agentApprovalState } from '../agentApprovalState';
import { importRunState } from '../importRunState';
import {
  buildEditedProcessDocument,
  closeMappingReview,
  setDocumentLocale,
} from './mappingReviewSession';
import { referenceKeyOccurrences } from './referenceKeyOccurrences';
import { mappingReviewLabels } from './mappingReviewLabels';
import type {
  MappingReviewSession,
  MappingReviewTableSlice,
} from './mappingReviewState';
import type { ReferenceValuesTarget } from './MappingReviewColumns';
import type { ReviewSection } from './MappingReviewSections';

const { Text } = Typography;

const warningLinkStyle: CSSProperties = {
  whiteSpace: 'normal',
  height: 'auto',
  textAlign: 'start',
};

const AUTO = 'auto';

const NUMBER_LOCALE_OPTIONS = [
  { value: AUTO, label: 'Auto' },
  { value: 'dotDecimal', label: '1,234.56 (dot decimal)' },
  { value: 'commaDecimal', label: '1.234,56 (comma decimal)' },
  { value: 'indianComma', label: '1,23,456.78 (Indian)' },
] as const;

const DATE_LOCALE_OPTIONS = [
  { value: AUTO, label: 'Auto' },
  { value: 'dayFirst', label: 'DD/MM/YYYY (day first)' },
  { value: 'monthFirst', label: 'MM/DD/YYYY (month first)' },
  { value: 'yearFirst', label: 'YYYY-MM-DD (year first)' },
] as const;

/** Fixed UTC offsets only — the coercer's `dateZone` supports offsets, not IANA zones. */
const DATE_ZONE_OPTIONS = [
  { value: AUTO, label: 'Auto' },
  { value: 'naive', label: 'Naive (UTC)' },
  { value: '-12:00', label: 'UTC-12:00' },
  { value: '-11:00', label: 'UTC-11:00' },
  { value: '-10:00', label: 'UTC-10:00' },
  { value: '-09:00', label: 'UTC-09:00' },
  { value: '-08:00', label: 'UTC-08:00' },
  { value: '-07:00', label: 'UTC-07:00' },
  { value: '-06:00', label: 'UTC-06:00' },
  { value: '-05:00', label: 'UTC-05:00' },
  { value: '-04:00', label: 'UTC-04:00' },
  { value: '-03:00', label: 'UTC-03:00' },
  { value: '-02:00', label: 'UTC-02:00' },
  { value: '-01:00', label: 'UTC-01:00' },
  { value: '+00:00', label: 'UTC+00:00' },
  { value: '+01:00', label: 'UTC+01:00' },
  { value: '+02:00', label: 'UTC+02:00' },
  { value: '+03:00', label: 'UTC+03:00' },
  { value: '+03:30', label: 'UTC+03:30' },
  { value: '+04:00', label: 'UTC+04:00' },
  { value: '+04:30', label: 'UTC+04:30' },
  { value: '+05:00', label: 'UTC+05:00' },
  { value: '+05:30', label: 'UTC+05:30' },
  { value: '+05:45', label: 'UTC+05:45' },
  { value: '+06:00', label: 'UTC+06:00' },
  { value: '+06:30', label: 'UTC+06:30' },
  { value: '+07:00', label: 'UTC+07:00' },
  { value: '+08:00', label: 'UTC+08:00' },
  { value: '+08:45', label: 'UTC+08:45' },
  { value: '+09:00', label: 'UTC+09:00' },
  { value: '+09:30', label: 'UTC+09:30' },
  { value: '+10:00', label: 'UTC+10:00' },
  { value: '+10:30', label: 'UTC+10:30' },
  { value: '+11:00', label: 'UTC+11:00' },
  { value: '+12:00', label: 'UTC+12:00' },
  { value: '+12:45', label: 'UTC+12:45' },
  { value: '+13:00', label: 'UTC+13:00' },
  { value: '+14:00', label: 'UTC+14:00' },
] as const;

const LOCALE_SOURCE_LABEL = {
  agent: 'from agent',
  user: 'you set',
  orgDefault: 'org default',
  inferred: 'detected',
} as const;

const FOCUSABLE_SELECTOR =
  'input, button, select, textarea, a[href], [tabindex]:not([tabindex="-1"])';

interface SliceStats {
  groupId: string;
  columnsMapped: number;
  rowsTotal: number;
  rejected: number;
  /** Rows the classifier tagged as noise — always skipped by the import. */
  noise: number;
  willWrite: number;
  /** AI-proposed columns whose confidence was below the warn threshold. */
  flaggedLowConfidence: number;
  /** Source columns still below threshold and not yet touched by the user. */
  lowConfidenceSources: string[];
  unresolvedReferences: number;
  /** Source columns with at least one unresolved reference value. */
  unresolvedSources: string[];
  /** Source columns present in the data but mapped to no target field. */
  unmappedSources: string[];
  removed: boolean;
}

function sliceStats(slice: Snapshot<MappingReviewTableSlice>): SliceStats {
  const rowsTotal = slice.baseRows.length;
  const rejected = new Set(
    Object.keys(slice.rejectedRows)
      .map(Number)
      .filter((i) => i < rowsTotal),
  );
  const baseBySource = new Map(
    slice.baseColumns.map((c) => [c.sourceColumn, c]),
  );
  const flaggedLowConfidence = slice.baseColumns.filter(
    (c) => c.confidence < CONFIDENCE_WARN_THRESHOLD,
  ).length;
  const lowConfidenceSources = slice.columns
    .filter((c) => {
      const base = baseBySource.get(c.sourceColumn);
      const manual = !base || base.targetField !== c.targetField;
      return !manual && c.confidence < CONFIDENCE_WARN_THRESHOLD;
    })
    .map((c) => c.sourceColumn);

  let unresolved = 0;
  const unresolvedSources: string[] = [];
  for (const column of slice.columns) {
    if (!column.resolution) continue;
    const keys = referenceKeyOccurrences({
      chunks: slice.chunks,
      sourceColumn: column.sourceColumn,
      cellOverrides: slice.cellOverrides,
      rejected,
      rowLimit: rowsTotal,
    });
    const colUnresolved = [...keys.keys()].filter(
      (key) => !column.resolution?.[key],
    ).length;
    unresolved += colUnresolved;
    if (colUnresolved > 0) unresolvedSources.push(column.sourceColumn);
  }

  const sourceColumns = new Set(slice.chunks.flatMap((c) => c.sourceColumns));
  const mapped = new Set(slice.columns.map((c) => c.sourceColumn));
  const unmappedSources = [...sourceColumns].filter((s) => !mapped.has(s));

  const willWrite =
    slice.columns.length === 0
      ? 0
      : slice.baseRows.filter((r, i) => r.tag !== 'junk' && !rejected.has(i))
          .length;
  const noise = slice.baseRows.filter((r) => r.tag === 'junk').length;
  const rejectedNonJunk = [...rejected].filter(
    (i) => slice.baseRows[i]?.tag !== 'junk',
  ).length;

  return {
    groupId: slice.groupId,
    columnsMapped: slice.columns.length,
    rowsTotal,
    rejected: rejectedNonJunk,
    noise,
    willWrite,
    flaggedLowConfidence,
    lowConfidenceSources,
    unresolvedReferences: unresolved,
    unresolvedSources,
    unmappedSources,
    removed: slice.targetRemoved,
  };
}

interface MappingReviewApproveProps {
  session: MappingReviewSession;
  section: ReviewSection;
  onSectionChange: (section: ReviewSection) => void;
  onOpenReferenceValues: (target: ReferenceValuesTarget) => void;
}

const MappingReviewApprove: FC<MappingReviewApproveProps> = ({
  session,
  section,
  onSectionChange,
  onOpenReferenceValues,
}) => {
  const { token } = theme.useToken();
  const snap = useSnapshot(session);
  const approvalSnap = useSnapshot(agentApprovalState);
  const formSnap = useSnapshot(formStore.state);
  const importRun = useSnapshot(importRunState).run;
  const formTypes = useFormTypes();
  const { messageApi } = useContext(MessageContext);
  const { changeImportFormType, discardImportRun } = useContext(AIContext);
  const { getHasNavigatedAway } = useAgentContext();
  const [createVersion, setCreateVersion] = useState(false);
  const [newState, setNewState] = useState<FormStateId | undefined>(undefined);
  const [queueCursor, setQueueCursor] = useState<Record<string, number>>({});
  const [stage, setStage] = useState<'saving' | 'submitting' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isDocument = snap.target?.kind === 'document';
  const importSlice = snap.import;

  const sliceStatsList = useMemo<SliceStats[]>(() => {
    if (importSlice) return [sliceStats(importSlice)];
    if (isDocument)
      return snap.tableOrder.map((groupId) => sliceStats(snap.tables[groupId]));
    return [];
  }, [importSlice, isDocument, snap.tableOrder, snap.tables]);

  const activeSlices = sliceStatsList.filter((s) => !s.removed);
  const rowsWillWrite = activeSlices.reduce((n, s) => n + s.willWrite, 0);
  const flaggedLowConfidence = activeSlices.reduce(
    (n, s) => n + s.flaggedLowConfidence,
    0,
  );
  const lowConfidenceColumns = activeSlices.reduce(
    (n, s) => n + s.lowConfidenceSources.length,
    0,
  );
  const addressedLowConfidence = Math.max(
    0,
    flaggedLowConfidence - lowConfidenceColumns,
  );
  const unresolvedReferences = activeSlices.reduce(
    (n, s) => n + s.unresolvedReferences,
    0,
  );
  const unmappedColumns = activeSlices.reduce(
    (n, s) => n + s.unmappedSources.length,
    0,
  );
  const anyColumnsMapped = activeSlices.some((s) => s.columnsMapped > 0);

  const lowConfidencePct = Math.round(CONFIDENCE_WARN_THRESHOLD * 100);
  const lowConfidenceQueue = activeSlices.flatMap((s) =>
    s.lowConfidenceSources.map((sourceColumn) => ({
      groupId: s.groupId,
      sourceColumn,
    })),
  );
  const unresolvedQueue = activeSlices.flatMap((s) =>
    s.unresolvedSources.map((sourceColumn) => ({
      groupId: s.groupId,
      sourceColumn,
    })),
  );
  const unmappedQueue = activeSlices.flatMap((s) =>
    s.unmappedSources.map((sourceColumn) => ({
      groupId: s.groupId,
      sourceColumn,
    })),
  );

  const keptFields = isDocument
    ? Object.values(snap.fields).filter((f) => !f.removed).length
    : 0;
  const keptReferences = isDocument
    ? Object.values(snap.references).filter((r) => !r.removed).length
    : 0;
  const slotQueue = isDocument
    ? Object.values(snap.references)
        .filter((r) => !r.removed && r.resolution.kind !== 'entity')
        .map((r) => r.slotKey)
    : [];
  const unresolvedSlotReferences = slotQueue.length;
  const tablesAssigned = activeSlices.length;

  const binding =
    snap.target?.kind === 'document' ? snap.target.binding : undefined;
  const targetFormVersionId =
    binding?.kind === 'formVersion' ? binding.formVersionId : undefined;
  const targetFormId =
    binding?.kind === 'formVersion' ? binding.formId : undefined;

  // A dropped-file import bound to the form the run just created: offer a target-type override.
  const isImportRun =
    isDocument &&
    binding?.kind === 'formVersion' &&
    !!importRun &&
    importRun.formVersionId === binding.formVersionId;

  const formMatches =
    Boolean(targetFormVersionId) && formSnap.versionId === targetFormVersionId;
  const currentState = formMatches
    ? formSnap.formVersion.data?.state
    : undefined;
  const allowedNextStates = currentState
    ? (formSnap.formType.data?.possibleStates?.[currentState]
        ?.allowedNextStates ?? [])
    : [];
  const versionCheckboxEnabled =
    Boolean(targetFormVersionId) && allowedNextStates.length > 0;
  const versionTooltip = !targetFormVersionId
    ? 'Versions apply only to a form-bound apply'
    : allowedNextStates.length === 0
      ? 'Open the bound form to choose the new version state'
      : undefined;
  const selectedState = newState ?? allowedNextStates[0];

  const finishResolution = (
    approved: boolean,
    result: AgenticApprovalResolvedResponse | undefined,
  ) => {
    if (result?.newFormVersionId && targetFormId) {
      formActions.navigateToVersion(targetFormId, result.newFormVersionId);
    }
    agentApprovalState.resolved = {
      toolCallId: session.approval.toolCallId,
      summary: session.approval.summary,
      approved,
      ...(session.approval.counts
        ? { counts: { ...session.approval.counts } }
        : {}),
    };
    agentApprovalState.pending = null;
    agentApprovalState.submitting = false;
    closeMappingReview();
  };

  const postResolution = async (approved: boolean) => {
    const pending = agentApprovalState.pending;
    if (!pending || pending.toolCallId !== session.approval.toolCallId) {
      agentApprovalState.submitting = false;
      messageApi?.warning?.(
        'This approval is no longer active — your saved review was not submitted.',
      );
      closeMappingReview();
      return;
    }
    const result = await callRunLanguageModel({
      approvalResolved: {
        traceId: pending.traceId,
        toolCallId: pending.toolCallId,
        approved,
        ...(getHasNavigatedAway() ? { userNavigatedAway: true } : {}),
        ...(approved && createVersion && versionCheckboxEnabled && selectedState
          ? { createNewFormVersion: { newState: selectedState } }
          : {}),
      },
    });
    finishResolution(
      approved,
      result && typeof result === 'object' && 'resolved' in result
        ? (result as AgenticApprovalResolvedResponse)
        : undefined,
    );
  };

  const fail = (message: string) => {
    session.saving = false;
    agentApprovalState.submitting = false;
    setStage(null);
    setError(message);
    messageApi?.error?.(message);
  };

  const approve = async () => {
    if (session.saving || agentApprovalState.submitting) return;
    setError(null);
    session.saving = true;
    agentApprovalState.submitting = true;
    setStage('saving');
    try {
      if (session.baseDoc) {
        await saveProcessDocument(
          session.fileFolder,
          session.processIndex,
          buildEditedProcessDocument(session),
        );
      }
    } catch (e) {
      console.error(
        `Failed to save mapping review edits (fileFolder=${session.fileFolder}, processIndex=${session.processIndex}):`,
        e,
      );
      fail(
        e instanceof Error ? e.message : 'Failed to save mapping review edits',
      );
      return;
    }
    setStage('submitting');
    try {
      await postResolution(true);
    } catch (e) {
      console.error(
        `Failed to send mapping review approval (toolCallId=${session.approval.toolCallId}):`,
        e,
      );
      fail(e instanceof Error ? e.message : 'Failed to send approval response');
    }
  };

  const cancel = async () => {
    if (session.saving || agentApprovalState.submitting) return;
    setError(null);
    session.saving = true;
    agentApprovalState.submitting = true;
    setStage('submitting');
    try {
      await postResolution(false);
      await discardImportRun();
    } catch (e) {
      console.error(
        `Failed to send mapping review cancellation (toolCallId=${session.approval.toolCallId}):`,
        e,
      );
      fail(e instanceof Error ? e.message : 'Failed to send approval response');
    }
  };

  const afterSection = (target: ReviewSection, run: () => void) => {
    if (isDocument && section !== target) onSectionChange(target);
    requestAnimationFrame(() => requestAnimationFrame(run));
  };

  const focusElement = (el: Element | null | undefined) => {
    if (!(el instanceof HTMLElement)) return;
    const focusable = el.matches(FOCUSABLE_SELECTOR)
      ? el
      : (el.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ?? el);
    focusable.scrollIntoView({ behavior: 'smooth', block: 'center' });
    focusable.focus({ preventScroll: true });
  };

  const focusColumn = (groupId: string, sourceColumn: string) =>
    afterSection('tables', () => {
      const cards = [...document.querySelectorAll('.mapping-review-columns')];
      const card =
        cards.find((c) => c.getAttribute('data-group-id') === groupId) ??
        cards[0];
      const field =
        card &&
        ([...card.querySelectorAll('[aria-label]')].find(
          (el) =>
            el.getAttribute('aria-label') ===
            `Map source column ${sourceColumn}`,
        ) ??
          card);
      focusElement(field);
    });

  const focusReferenceSlot = (slotKey: string) =>
    afterSection('fields', () => {
      const region = document.querySelector('.mapping-review-fields');
      focusElement(
        region?.querySelector(`[data-slot-key="${slotKey}"]`) ?? region,
      );
    });

  const openReferenceValues = (target: ReferenceValuesTarget) =>
    afterSection('tables', () => onOpenReferenceValues(target));

  const advanceQueue = <T,>(
    key: string,
    queue: T[],
    act: (item: T) => void,
  ) => {
    if (queue.length === 0) return;
    act(queue[(queueCursor[key] ?? 0) % queue.length]);
    setQueueCursor((c) => ({ ...c, [key]: (c[key] ?? 0) + 1 }));
  };

  const submitting = snap.saving || approvalSnap.submitting;
  const stageLabel =
    stage === 'saving' ? 'Saving edits…' : submitting ? 'Submitting…' : null;
  // A document apply can be field-only (no tables); an import always needs a mapped column.
  const nothingToApply = isDocument
    ? keptFields === 0 && keptReferences === 0 && rowsWillWrite === 0
    : !anyColumnsMapped || rowsWillWrite === 0;
  const approveDisabled = submitting || nothingToApply;
  const approveTooltip = nothingToApply
    ? 'Nothing to apply — keep at least one field, reference, or row, or Cancel'
    : undefined;
  const approveLabel =
    rowsWillWrite > 0
      ? `Approve & apply ${rowsWillWrite} row${rowsWillWrite !== 1 ? 's' : ''}`
      : 'Approve';

  const lowReviewed = flaggedLowConfidence > 0 && lowConfidenceColumns === 0;
  const hasWarnings =
    lowConfidenceColumns > 0 ||
    unresolvedReferences > 0 ||
    unmappedColumns > 0 ||
    unresolvedSlotReferences > 0;
  const importSlice0 = activeSlices[0];

  const sourceHint = (source: LocaleSource | undefined) =>
    source ? (
      <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
        {LOCALE_SOURCE_LABEL[source]}
      </Text>
    ) : null;
  const encoding = snap.encoding;

  return (
    <FlexCol
      noGrow
      gap={10}
      style={{
        position: 'sticky',
        bottom: 0,
        background: token.colorBgContainer,
        borderTop: `1px solid ${token.colorBorderSecondary}`,
        padding: 10,
        zIndex: 20,
      }}
    >
      {isDocument ? (
        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          {keptFields} field{keptFields !== 1 ? 's' : ''} · {keptReferences}{' '}
          reference
          {keptReferences !== 1 ? 's' : ''} · {tablesAssigned} table
          {tablesAssigned !== 1 ? 's' : ''} → {rowsWillWrite} table row
          {rowsWillWrite !== 1 ? 's' : ''}
          {snap.dirty ? ' · edited' : ''}
        </Text>
      ) : (
        importSlice0 && (
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {importSlice0.columnsMapped} column
            {importSlice0.columnsMapped !== 1 ? 's' : ''} mapped ·{' '}
            {importSlice0.rowsTotal} row
            {importSlice0.rowsTotal !== 1 ? 's' : ''} in · {importSlice0.noise}{' '}
            noise · {importSlice0.rejected} rejected → {importSlice0.willWrite}{' '}
            will write
            {snap.dirty ? ' · edited' : ''}
          </Text>
        )
      )}
      <FlexBox noGrow justifyStart gap={10} wrap>
        <FlexBox noGrow justifyStart gap={5} wrap>
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            Number format
          </Text>
          <Select
            size="small"
            aria-label="Number format for numeric columns"
            value={snap.locale ?? AUTO}
            disabled={submitting}
            onChange={(value: string) =>
              setDocumentLocale({
                locale: value === AUTO ? null : (value as NumberLocale),
              })
            }
            options={NUMBER_LOCALE_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            style={{ minWidth: 'min(190px, 100%)' }}
          />
          {sourceHint(snap.localeSource?.number)}
        </FlexBox>
        <FlexBox noGrow justifyStart gap={5} wrap>
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            Date order
          </Text>
          <Select
            size="small"
            aria-label="Date order for date columns"
            value={snap.dateLocale ?? AUTO}
            disabled={submitting}
            onChange={(value: string) =>
              setDocumentLocale({
                dateLocale: value === AUTO ? null : (value as DateLocale),
              })
            }
            options={DATE_LOCALE_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            style={{ minWidth: 'min(190px, 100%)' }}
          />
          {sourceHint(snap.localeSource?.date)}
        </FlexBox>
        <FlexBox noGrow justifyStart gap={5} wrap>
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            Century pivot
          </Text>
          <InputNumber
            size="small"
            aria-label="Two-digit year century pivot"
            value={snap.centuryPivot ?? null}
            min={1900}
            max={2100}
            precision={0}
            placeholder="Auto"
            disabled={submitting}
            onChange={(value) => {
              if (value == null || (value >= 1900 && value <= 2100)) {
                setDocumentLocale({ centuryPivot: value ?? null });
              }
            }}
            style={{ width: 90, maxWidth: '100%' }}
          />
          {sourceHint(snap.localeSource?.centuryPivot)}
        </FlexBox>
        <FlexBox noGrow justifyStart gap={5} wrap>
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            Time zone
          </Text>
          <Select
            size="small"
            showSearch
            aria-label="Time zone offset for zone-less date-times"
            value={snap.dateZone ?? AUTO}
            disabled={submitting}
            onChange={(value: string) =>
              setDocumentLocale({ dateZone: value === AUTO ? null : value })
            }
            options={DATE_ZONE_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            style={{ minWidth: 'min(130px, 100%)' }}
          />
          {sourceHint(snap.localeSource?.dateZone)}
        </FlexBox>
        {encoding && (encoding.chosen || encoding.failed) && (
          <FlexBox noGrow justifyStart gap={5}>
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              Encoding
            </Text>
            {encoding.chosen && (
              <Tag style={{ margin: 0 }}>Decoded as {encoding.chosen}</Tag>
            )}
            {encoding.failed && (
              <Text type="warning" style={{ fontSize: token.fontSizeSM }}>
                Decoding fell back lossily — some characters may be wrong
              </Text>
            )}
          </FlexBox>
        )}
      </FlexBox>
      {(hasWarnings || lowReviewed) && (
        <FlexCol noGrow gap={5} aria-live="polite">
          <FlexBox noGrow justifyStart gap={5} wrap>
            {lowConfidenceColumns > 0 && (
              <Button
                type="link"
                size="small"
                style={warningLinkStyle}
                aria-label={`Review next of ${lowConfidenceColumns} low-confidence column${lowConfidenceColumns !== 1 ? 's' : ''} below ${lowConfidencePct}%`}
                onClick={() =>
                  advanceQueue(
                    'low',
                    lowConfidenceQueue,
                    ({ groupId, sourceColumn }) =>
                      focusColumn(groupId, sourceColumn),
                  )
                }
              >
                <span aria-hidden="true">⚠ </span>
                {lowConfidenceColumns} low-confidence (below {lowConfidencePct}
                %) — review
              </Button>
            )}
            {unresolvedReferences > 0 && (
              <Button
                type="link"
                size="small"
                style={warningLinkStyle}
                aria-label={`Review next of ${unresolvedReferences} unresolved column value${unresolvedReferences !== 1 ? 's' : ''}`}
                onClick={() =>
                  advanceQueue(
                    'refs',
                    unresolvedQueue,
                    ({ groupId, sourceColumn }) =>
                      openReferenceValues({
                        groupId,
                        sourceColumn,
                        filter: 'unresolved',
                      }),
                  )
                }
              >
                <span aria-hidden="true">⚠ </span>
                {mappingReviewLabels.unresolvedColumnValues(
                  unresolvedReferences,
                )}
              </Button>
            )}
            {unresolvedSlotReferences > 0 && (
              <Button
                type="link"
                size="small"
                style={warningLinkStyle}
                aria-label={`Review next of ${unresolvedSlotReferences} unresolved reference field${unresolvedSlotReferences !== 1 ? 's' : ''}`}
                onClick={() =>
                  advanceQueue('slots', slotQueue, (slotKey) =>
                    focusReferenceSlot(slotKey),
                  )
                }
              >
                <span aria-hidden="true">⚠ </span>
                {mappingReviewLabels.unresolvedReferenceFields(
                  unresolvedSlotReferences,
                )}
              </Button>
            )}
            {unmappedColumns > 0 && (
              <Button
                type="link"
                size="small"
                style={warningLinkStyle}
                aria-label={`Review next of ${unmappedColumns} unmapped column${unmappedColumns !== 1 ? 's' : ''}`}
                onClick={() =>
                  advanceQueue(
                    'unmapped',
                    unmappedQueue,
                    ({ groupId, sourceColumn }) =>
                      focusColumn(groupId, sourceColumn),
                  )
                }
              >
                {unmappedColumns} unmapped
              </Button>
            )}
          </FlexBox>
          {lowReviewed && (
            <Text type="success" style={{ fontSize: token.fontSizeSM }}>
              All flagged mappings reviewed ✓
            </Text>
          )}
          {lowConfidenceColumns > 0 && addressedLowConfidence > 0 && (
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {addressedLowConfidence} addressed by you
            </Text>
          )}
        </FlexCol>
      )}
      <Divider style={{ margin: 0 }} />
      {error && (
        <Alert
          type="error"
          showIcon
          closable={{ onClose: () => setError(null) }}
          title={error}
          aria-label="Approval error"
        />
      )}
      <FlexBox noGrow stretch wrap>
        <FlexBox noGrow justifyStart gap={5} wrap>
          {isImportRun && importRun && (
            <>
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                Target
              </Text>
              <Select
                size="small"
                aria-label="Target form type"
                value={importRun.formType}
                disabled={submitting}
                onChange={(value: string) => void changeImportFormType(value)}
                options={formTypes.data.map((ft) => ({
                  value: ft.docId as string,
                  label: ft.description?.shortLabel || ft.docId,
                }))}
                style={{ minWidth: 'min(140px, 100%)' }}
              />
            </>
          )}
          <Tooltip title={versionTooltip}>
            <Checkbox
              checked={createVersion && versionCheckboxEnabled}
              disabled={!versionCheckboxEnabled}
              onChange={(e) => setCreateVersion(e.target.checked)}
              aria-label="Create form version on approve"
            >
              Create form version
            </Checkbox>
          </Tooltip>
          {createVersion && versionCheckboxEnabled && (
            <Select
              size="small"
              aria-label="New form version state"
              value={selectedState}
              onChange={(value: FormStateId) => setNewState(value)}
              options={allowedNextStates.map((state) => ({
                value: state,
                label: formStateDescription[state]?.shortLabel ?? state,
              }))}
              style={{ minWidth: 'min(120px, 100%)' }}
            />
          )}
        </FlexBox>
        <FlexBox noGrow justifyEnd gap={10}>
          {stageLabel && (
            <Text
              type="secondary"
              style={{ fontSize: token.fontSizeSM }}
              aria-live="polite"
            >
              {stageLabel}
            </Text>
          )}
          <Button
            size="small"
            onClick={() => void cancel()}
            disabled={submitting}
            aria-label="Cancel reviewed agent action"
          >
            Cancel
          </Button>
          <Tooltip title={approveTooltip}>
            <Button
              type="primary"
              loading={snap.saving}
              disabled={approveDisabled}
              onClick={() => void approve()}
              aria-label="Approve reviewed agent action"
            >
              {approveLabel}
              {createVersion && versionCheckboxEnabled
                ? ' into new version'
                : ''}
            </Button>
          </Tooltip>
        </FlexBox>
      </FlexBox>
    </FlexCol>
  );
};

export default MappingReviewApprove;
